import { useState } from "react";
import {
  Alert,
  Box,
  Container,
  ExpandableSection,
  TextContent,
} from "@amzn/awsui-components-react";
import PublishStatusComponent from "./PublishStatusComponent";
import {
  cbeWithoutPublishStatus,
  getAccountingOperationColor,
} from "../../helpers/cbeSummaryUtils";
import CbeSummary from "./CbeSummary";
import { CanonicalBusinessEvent } from "../../types";
import { CBERecord } from "../../types/cbes";
import { JsonModal } from "../common/JsonModal";
import { useMatchSets } from "../../contexes/MatchSetsContext";
import { extractMatchKeyFromRecord } from "../../helpers/matchKeyUtils";
import { MatchSetState } from "../../types/matchSetState";
import { setModalParam, clearModalParam } from "../../helpers/modalUrlUtils";
import { ModalSubtitle } from "../common/ModalSubtitle";

interface Props {
  cbe: CanonicalBusinessEvent;
  allCbes: CanonicalBusinessEvent[];
}

function CbeMatchSetSummary({ cbe }: { cbe: CanonicalBusinessEvent }) {
  const { matchSets, isLoading } = useMatchSets();

  const cbeMatchKeys = new Set<string>();
  const records = cbe.records || {};
  const recordList = Array.isArray(records) ? records : Object.values(records);
  recordList.forEach((r: CBERecord) => {
    const mk = extractMatchKeyFromRecord(r);
    if (mk) cbeMatchKeys.add(mk);
    const attrs = r.clientAttributes ?? r.content?.clientAttributes;
    const defId = attrs?.deferral_id?.stringValue;
    if (defId) cbeMatchKeys.add(defId);
  });

  if (cbeMatchKeys.size === 0) return null;

  if (isLoading) {
    return (
      <>
        <br />
        <small>
          Match Sets:{" "}
          <Box color="text-status-inactive" variant="small" display="inline">
            Loading…
          </Box>
        </small>
      </>
    );
  }

  if (!matchSets || matchSets.length === 0) return null;

  const relevant = matchSets.filter((ms) => cbeMatchKeys.has(ms.matchKey));
  if (relevant.length === 0) return null;

  const closed = relevant.filter(
    (ms) => ms.state === MatchSetState.CLOSED
  ).length;
  const pending = relevant.filter(
    (ms) => ms.state === MatchSetState.PENDING
  ).length;
  const open = relevant.length - closed - pending;

  return (
    <>
      <br />
      <small>
        Match Sets:{" "}
        {open > 0 && closed === 0 && pending === 0 && (
          <Box color="text-status-warning" variant="small" display="inline">
            OPEN
          </Box>
        )}
        {closed > 0 && open === 0 && pending === 0 && (
          <Box color="text-status-success" variant="small" display="inline">
            CLOSED
          </Box>
        )}
        {pending > 0 && open === 0 && closed === 0 && (
          <Box color="text-status-inactive" variant="small" display="inline">
            PENDING
          </Box>
        )}
        {(open > 0 && (closed > 0 || pending > 0)) ||
        (closed > 0 && pending > 0) ? (
          <>
            {open > 0 && (
              <Box color="text-status-warning" variant="small" display="inline">
                {open} OPEN
              </Box>
            )}
            {open > 0 && (closed > 0 || pending > 0) && ", "}
            {closed > 0 && (
              <Box color="text-status-success" variant="small" display="inline">
                {closed} CLOSED
              </Box>
            )}
            {closed > 0 && pending > 0 && ", "}
            {pending > 0 && (
              <Box
                color="text-status-inactive"
                variant="small"
                display="inline"
              >
                {pending} PENDING
              </Box>
            )}
          </>
        ) : null}
      </small>
    </>
  );
}

export default function CbeEntry(props: Props) {
  const { cbe } = props;
  const [isJsonModalVisible, setIsJsonModalVisible] = useState(false);

  const hasRecords = Array.isArray(cbe.records)
    ? cbe.records.length > 0
    : Object.keys(cbe.records || {}).length > 0;

  return (
    <Container>
      <PublishStatusComponent cbe={cbe} />
      <ExpandableSection
        variant="default"
        header={
          <Box variant="h4">
            {cbe.idempotenceId} (V{cbe.idempotenceVersion})
            <br />
            {cbe.creationDate && (
              <TextContent>
                <small>
                  Accounting Operation:{" "}
                  <Box
                    color={getAccountingOperationColor(cbe.accountingOperation)}
                    variant="small"
                  >
                    {cbe.accountingOperation}
                  </Box>
                </small>{" "}
                <br />
                <small>Destination: {cbe.destination} </small>
                <br />
                <small>
                  Created on: {new Date(cbe.creationDate).toLocaleString()}
                </small>
                <br />
                <small>
                  PK: {cbe.pk}, SK: {cbe.sk}
                </small>
                <br />
                <small>Source Group Version: {cbe.sourceGroupVersion}</small>
                <CbeMatchSetSummary cbe={cbe} />
              </TextContent>
            )}
          </Box>
        }
      >
        {hasRecords ? (
          <CbeSummary
            cbe={cbe}
            allCbes={props.allCbes}
            modalHandler={() => {
              setIsJsonModalVisible(true);
              setModalParam("cbe-json", { cbeId: cbe.idempotenceId, cbeV: String(cbe.idempotenceVersion) });
            }}
          />
        ) : (
          <Alert type="info" header="No Records">
            There are no records to display for this entry.
          </Alert>
        )}
      </ExpandableSection>

      {isJsonModalVisible && (
        <JsonModal
          title={<>CBE · V{cbe.idempotenceVersion} · {cbe.destination}<ModalSubtitle text={cbe.idempotenceId} /></>}
          content={cbeWithoutPublishStatus(cbe)}
          visible={isJsonModalVisible}
          onDismiss={() => { setIsJsonModalVisible(false); clearModalParam(); }}
          initialCollapsed={2}
        />
      )}
    </Container>
  );
}
